import { Cancel, Close, Save } from '@mui/icons-material';
import {
  AppBar,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  DialogContent,
  Divider,
  Grid,
  Paper,
  Toolbar,
  TextField,
  FormControl,
  Autocomplete,
  createFilterOptions,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { grey } from '@mui/material/colors';
import {
  getPersonMiddleNames,
} from '../../apis/profileApis';
import { updateBuyerInfo } from '../../apis/buyersGuideApis';
import { deNormalizePhoneNumber } from '../Profile/utils';

const filter = createFilterOptions();

const EditBuyerRenterInformationDialog = ({ open, onClose, onSave, data }) => {

  const theme = useTheme();
  const { t } = useTranslation();
  const isMobileOrTab = useMediaQuery(theme.breakpoints.down('md'));
  const [value, setValue] = useState({});
  const [dropdownValues, setDropdownValues] = useState({});
  const [errors, setErrors] = useState({})
 
  const isP2Available = useMemo(()=>{
    return data?.p2?.personId
  },[data?.p2])

  const clearError = (name) => {
    const [personKey, field] = name.split('.');
    setErrors((prevErrors) => {
      if (!prevErrors[personKey]) return prevErrors;
      const { [field]: _, ...restFields } = prevErrors[personKey];
      const newErrors = { ...prevErrors };
      if (Object.keys(restFields).length === 0) {
        delete newErrors[personKey];
      } else {
        newErrors[personKey] = restFields;
      }
      return newErrors;
    });
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    const [personKey, field] = name.split('.');
    if (
      (field === "firstName" || field === "initials") &&
      value.trim()
    ) {
      setErrors(prev => ({
        ...prev,
        [personKey]: {
          ...prev[personKey],
          firstName: undefined,
          initials: undefined,
        },
      }));
    }
    setValue((prev) => ({
      ...prev,
      [personKey]: {
        ...prev[personKey],
        [field]: value,
      },
    }));

    clearError(name);
  };


  const phoneRegex = /^\+[1-9]\d{7,14}$/;

  const handlePhoneChange = (e) => {
    let { name, value } = e.target;
    const [personKey, field] = name.split('.');

    // update value first
    setValue(prev => ({
      ...prev,
      [personKey]: {
        ...prev[personKey],
        [field]: value
      }
    }));
    // validate
    if (!phoneRegex.test(value)) {
      setErrors(prev => ({
        ...prev,
        [personKey]: {
          ...(prev[personKey] || {}),
          [field]: t('buyersGuide.quotation.generate.formsValidation.phoneInvalid')
        }
      }));
    } else {
      // clear error
      setErrors(prev => ({
        ...prev,
        [personKey]: {
          ...(prev[personKey] || {}),
          [field]: undefined
        }
      }));
    }
  };


  const validateInfo = () => {
    const errors = {};
    let isValid = true
    const validateTelephone = (key, field, value) => {
      if (value && deNormalizePhoneNumber(value).length !== 10) {
        errors[key] = {
          ...(errors[key] || {}),
          [field]: t("general.field.error"),
        };
        isValid = false;
      }
    };
   const commonError = ({ prefixKey, prefixKeysValues }) => {

  if (!prefixKeysValues.lastName) {
    errors[prefixKey] = {
      ...(errors[prefixKey] || {}),
      lastName: t("general.field.error"),
    };
    isValid = false;
  }

  const hasFirstName = !!prefixKeysValues.firstName?.trim();
  const hasInitials = !!prefixKeysValues.initials?.trim();

  if (!hasFirstName && !hasInitials) {
    errors[prefixKey] = {
      ...(errors[prefixKey] || {}),
      firstName: t("general.field.error"),
      initials: t("general.field.error"),
    };
    isValid = false;
  }

  const prefix = prefixKeysValues.middleName;
  if (prefix) {
    const middleName = dropdownValues.middleNames.find(
      (m) => m.name === prefix
    );
    if (!middleName) {
      errors[prefixKey] = {
        ...(errors[prefixKey] || {}),
        middleName: t(
          "buyersGuide.quotation.generate.formsValidation.selectValueFromDropdown"
        ),
      };
      isValid = false;
    }
  }
};


    if (isBuyerAsPerson) {

      commonError({prefixKey:"p1" , prefixKeysValues:value.p1})
        const mobile = value.p1?.mobile

        validateTelephone("p1", "telephonePrivate", value.p1?.telephonePrivate);
        validateTelephone("p1", "telephoneWork", value.p1?.telephoneWork)
         
      if(mobile){
        if (!phoneRegex.test(mobile)) {
           errors.p1 = { ...(errors.p1 || {}), mobile: t('buyersGuide.quotation.generate.formsValidation.phoneInvalid')};
           isValid = isValid && false
        }
      }
      if (isP2Available) {
        const mobile = value.p2?.mobile
       commonError({prefixKey:"p2" , prefixKeysValues:value.p2})
       validateTelephone("p2", "telephonePrivate", value.p2?.telephonePrivate);
       validateTelephone("p2", "telephoneWork", value.p2?.telephoneWork)
         
      if(mobile){
        if (!phoneRegex.test(mobile)) {
           errors.p2 = { ...(errors.p2 || {}), mobile: t('buyersGuide.quotation.generate.formsValidation.phoneInvalid')};
           isValid = isValid && false
        }
      }
      }
      
    }
    else{
      commonError({prefixKey :"relation", prefixKeysValues: value.relation})
      const mobile = value.relation?.relationMobile
      if(mobile){
        if (!phoneRegex.test(mobile)) {
           errors.relation = { ...(errors.relation || {}), relationMobile: t('buyersGuide.quotation.generate.formsValidation.phoneInvalid')};
           isValid = isValid && false
        }
      }
       validateTelephone("relation","relationTelephone",value.relation?.relationTelephone
  );
    }

    setErrors(errors);
    return isValid;
  };


  useEffect(() => {
    const fetchDropdownValues = async () => {
      try {
        const [middleNames,] = await Promise.all([
          getPersonMiddleNames(),
        ]);

        setDropdownValues({
          middleNames: middleNames.data,
        });
      } catch (error) {
        console.error(t("error.header"));
      }
    };

    fetchDropdownValues();
  }, [t]);

  const buildPayload = () => {

    // CASE 1 — BUYER
    if (isBuyerAsPerson) {
      return {
        ...data,
        p1:{
          ...data.p1,
          firstName: value.p1?.firstName,
          lastName :value.p1?.lastName,
          initials :value.p1?.initials,
          middleName: dropdownValues?.middleNames?.find( (m) => m.name === value?.p1?.middleName) || null,      
          telephonePrivate :value.p1?.telephonePrivate,
          telephoneWork :value.p1?.telephoneWork,
          mobile :value.p1?.mobile,
          emailPrivate :value.p1?.emailPrivate,
          emailWork :value.p1?.emailWork,
        },
        p2:{
          ...data.p2,
          firstName: value.p2?.firstName,
          lastName :value.p2?.lastName,
          initials :value.p2?.initials,
          middleName: dropdownValues?.middleNames?.find( (m) => m.name === value?.p2?.middleName) || null,      
          telephonePrivate :value.p2?.telephonePrivate,
          telephoneWork :value.p2?.telephoneWork,
          mobile :value.p2?.mobile,
          emailPrivate :value.p2?.emailPrivate,
          emailWork :value.p2?.emailWork,
        }
      };
    }
    return {
      ...data,
       org:{
        ...data.org,
          firstName: value.relation?.firstName,
          lastName :value.relation?.lastName,
          initials :value.relation?.initials,
          middleName: dropdownValues?.middleNames?.find( (m) => m.name === value?.relation?.middleName) || null,      
          relationFunctionName :value.relation?.relationFunctionName,
          relationDepartment :value.relation?.relationDepartment,
          relationEmail :value.relation?.relationEmail,
          relationMobile :value.relation?.relationMobile,
          relationTelephone :value.relation?.relationTelephone,
       }
      }
    };


  const handleSaveRenterInfo = async () => {
    const isValidRenterInfo = validateInfo()
      if(isValidRenterInfo){
        const payload = buildPayload();  
    try {
      await updateBuyerInfo(payload);
      await onSave();
      onClose();

    } catch (e) {
      console.error(e);
    }
    }
  };


  useEffect(() => {
    if (!data) return;

    if (data.type === 0) {
      setValue({
        p1: {
          lastName: data.p1?.lastName || "",
          middleName: data.p1?.middleName?.name || "",
          initials: data.p1?.initials || "",
          firstName: data.p1?.firstName || "",
          telephonePrivate: data.p1?.telephonePrivate || "",
          telephoneWork: data.p1?.telephoneWork || "",
          mobile: data.p1?.mobile || "",
          emailPrivate: data.p1?.emailPrivate || "",
          emailWork: data.p1?.emailWork || "",
        },
        p2: isP2Available
          ? {
            lastName: data.p2?.lastName || "",
            middleName: data.p2?.middleName?.name || "",
            initials: data.p2?.initials || "",
            firstName: data.p2?.firstName || "",
            telephonePrivate: data.p2?.telephonePrivate || "",
            telephoneWork: data.p2?.telephoneWork || "",
            mobile: data.p2?.mobile || "",
            emailPrivate: data.p2?.emailPrivate || "",
            emailWork: data.p2?.emailWork || "",
          }
          : null,
      });
    }

    if (data.type === 1) {
      setValue({
        relation: {
          firstName: data.org?.firstName || "",
          lastName: data.org?.lastName || "",
          initials: data.org?.initials || "",
          middleName: data.org?.middleName?.name || "",
          relationFunctionName: data.org?.relationFunctionName || "",
          relationDepartment: data.org?.relationDepartment || "",
          relationEmail: data.org?.relationEmail || "",
          relationMobile: data.org?.relationMobile || "",
          relationTelephone: data.org?.relationTelephone || "",
        },
      });
    }
  }, [data, isP2Available]);

  const isBuyerAsPerson = useMemo(() => {
    return data.type === 0
  }, [data.type])


  return (
    <Dialog
      open={open}
      onClose={(event, reason) =>
        reason === 'backdropClick' ? event.stopPropagation() : onClose()
      }
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: {
          maxWidth: isP2Available && 'none',
          width: 1200,
          height: { md: 650 },
        },
      }}
      fullScreen={isMobileOrTab}
    >
      <AppBar position="relative" sx={{ boxShadow: 'none' }}>
        <Toolbar>
          <Stack
            direction={'row'}
            width={'100%'}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Stack direction={'row'} alignItems={'center'}>
              <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                <Close />
              </IconButton>
              <Typography sx={{ ml: 2 }} variant="h6" component="div">
                {t('buyersGuide.options.generate.buyerRenterInformation')}
              </Typography>
            </Stack>
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
              <IconButton
                onClick={onClose}
                sx={{ display: { sm: 'none' } }}
                color="inherit"
              >
                <Cancel />
              </IconButton>
              <Button
                onClick={onClose}
                size="small"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                variant="text"
                color="inherit"
                startIcon={<Cancel />}
              >
                {t('general.cancel')}
              </Button>
              <IconButton onClick={handleSaveRenterInfo} sx={{ display: { sm: 'none' } }} color="inherit">
                <Save />
              </IconButton>
              <Button
                onClick={handleSaveRenterInfo}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }} size="small" variant="text" color="inherit" startIcon={<Save />}
              >
                {t('general.save')}
              </Button>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ bgcolor: grey[100], mt: 0.3, p: 2 }}>
        <Paper sx={{ width: '100%', bgcolor: 'common.white' }} elevation={0}>
          <Typography variant="h6" sx={{ px: 2, py: 1.5 }}>
            {t('buyersGuide.options.generate.buyerRenterInformation')}
          </Typography>
          <Divider sx={{ my: 0 }} />
          <Grid container sx={{ py: 1 }} spacing={{ xs: 2, md: 0 }}>

            {isBuyerAsPerson && <Grid size={{ xs: 12, md: isP2Available ? 4 : 12 }} sx={{ borderRight: { xs: "none", sm: `1px dashed ${grey[200]}`, display: "flex", flexDirection: "column", justifyContent: "space-between" } }}>
              <Grid container spacing={1} sx={{ px: 2 }}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant='body1'>{t('general.person')} 1</Typography>
                </Grid>
                <Grid size={{ xs: 12 }} pb={1}>
                  <Divider />
                </Grid>
                <Grid container spacing={1} sx={{ px: 2 }}>
                  <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          name="p1.firstName"
                          label={t('general.firstName')}
                          value={value.p1?.firstName}
                          onChange={handleChange}
                          autoComplete="off"
                          size="small"
                          fullWidth
                          error={!!errors?.p1?.firstName}
                          helperText={errors?.p1?.firstName}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          name="p1.lastName"
                          label={t('general.lastName')}
                          value={value.p1?.lastName}
                          onChange={handleChange}
                          size="small"
                          autoComplete="off"
                          fullWidth
                          error={!!errors?.p1?.lastName}
                          helperText={errors?.p1?.lastName}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                         <FormControl size="small" fullWidth>
                            <Autocomplete
                              freeSolo
                              key={new Date().getTime()}
                              options={dropdownValues?.middleNames?.map(({ id, name }) =>  name)}
                              value={value.p1?.middleName}
                              filterOptions={(options, params) => {
                                  const filtered = filter(options, params);
                                  if (params.inputValue.includes(params.inputValue)) {
                                      filtered.push(params.inputValue);
                                  }
                                  return filtered;
                              }}
                              inputValue={value.p1?.middleName}
                             onInputChange={(e, newValue) => {
                              handleChange({
                                target: {
                                  name: "p1.middleName",
                                  value: newValue
                                }
                              });
                             }}
                              renderInput={(params) => (
                                  <TextField
                                      {...params}
                                      name="p1.middleName"
                                      label={t("general.namePrefixes")}
                                      variant="outlined"
                                      size="small"
                                      margin="dense"
                                      slotProps={{
                                          htmlInput: {
                                              ...params.inputProps,
                                              maxLength: 15
                                          },

                                          inputLabel: {
                                              shrink: true,
                                          }
                                      }}
                                      error={!!errors?.p1?.middleName}
                                      helperText={errors?.p1?.middleName}
                                      />
                              )}
                            
                          />
                            </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          name="p1.initials"
                          label={t('general.initials')}
                          value={value.p1?.initials}
                          onChange={handleChange}
                          autoComplete="off"
                          size="small"
                          fullWidth
                          error={!!errors?.p1?.initials}
                          helperText={errors?.p1?.initials}

                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          name="p1.telephonePrivate"
                          label={t('buyersGuide.buyersInfo.phonePrivate')}
                          value={deNormalizePhoneNumber(value.p1?.telephonePrivate)}
                          onChange={handleChange}
                          size="small"
                          autoComplete="off"
                          fullWidth
                          error={!!errors?.p1?.telephonePrivate}
                          helperText={errors?.p1?.telephonePrivate}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          name="p1.telephoneWork"
                          label={t('buyersGuide.buyersInfo.phoneWork')}
                          value={deNormalizePhoneNumber(value.p1?.telephoneWork)}
                          onChange={handleChange}
                          size="small"
                          autoComplete="off"
                          fullWidth
                          error={!!errors?.p1?.telephoneWork}
                          helperText={errors?.p1?.telephoneWork}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          name="p1.mobile"
                          label={t('buyersGuide.mobile')}
                          value={value.p1?.mobile}
                          onChange={handlePhoneChange}
                          size="small"
                          autoComplete="off"
                          fullWidth
                          error={!!errors?.p1?.mobile}
                          helperText={errors?.p1?.mobile}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          name="p1.emailPrivate"
                          label={t('general.emailPrivate')}
                          value={value.p1?.emailPrivate}
                          onChange={handleChange}
                          size="small"
                          autoComplete="off"
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          name="p1.emailWork"
                          label={t('buyersGuide.buyersInfo.emailWork')}
                          value={value.p1?.emailWork}
                          onChange={handleChange}
                          size="small"
                          autoComplete="off"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>}


            {(!isBuyerAsPerson || isP2Available) && (
              <Grid size={{ xs: 12, md: isP2Available ? 4 : 12 }} sx={{ borderRight: { xs: "none", sm: `1px dashed ${grey[200]}`, display: "flex", flexDirection: "column", justifyContent: "space-between" } }}>
                <Grid container spacing={1} sx={{ px: 2 }}>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body1">{!isBuyerAsPerson ? t('workOrders.relation') : `${t('general.person')} 2`} </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }} pb={1}>
                    <Divider />
                  </Grid>
                  {isBuyerAsPerson && isP2Available && (
                    <Grid container spacing={1} sx={{ px: 2 }}>
                      <Grid size={{ xs: 12 }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="p2.firstName"
                              label={t('general.firstName')}
                              value={value.p2?.firstName}
                              onChange={handleChange}
                              autoComplete="off"
                              size="small"
                              fullWidth
                              error={!!errors?.p2?.firstName}
                              helperText={errors?.p2?.firstName}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="p2.lastName"
                              label={t('general.lastName')}
                              value={value.p2?.lastName}
                              onChange={handleChange}
                              autoComplete="off"
                              size="small"
                              fullWidth
                              error={!!errors?.p2?.lastName}
                              helperText={errors?.p2?.lastName}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                             <FormControl size="small" fullWidth>
                               <Autocomplete
                              freeSolo
                              key={new Date().getTime()}
                              options={dropdownValues?.middleNames?.map(({ id, name }) =>  name)}
                              value={value.p2?.middleName}
                              filterOptions={(options, params) => {
                                  const filtered = filter(options, params);
                                  if (params.inputValue.includes(params.inputValue)) {
                                      filtered.push(params.inputValue);
                                  }
                                  return filtered;
                              }}
                              inputValue={value.p2?.middleName}
                              onInputChange={(e, newValue) => {
                              handleChange({
                                target: {
                                  name: "p2.middleName",
                                  value: newValue
                                }
                              });
                             }}
                              renderInput={(params) => (
                                  <TextField
                                      {...params}
                                      name="p2.middleName"
                                      label={t("general.namePrefixes")}
                                      variant="outlined"
                                      size="small"
                                      margin="dense"
                                    error={!!errors?.p2?.middleName}
                                     helperText={errors?.p2?.middleName}
                                      slotProps={{
                                          htmlInput: {
                                              ...params.inputProps,
                                              maxLength: 15
                                          },

                                          inputLabel: {
                                              shrink: true,
                                          }
                                      }} />
                              )}
                          />
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="p2.initials"
                              label={t('general.initials')}
                              value={value.p2?.initials}
                              onChange={handleChange}
                              autoComplete="off"
                              size="small"
                              fullWidth
                              error={!!errors?.p2?.initials}
                              helperText={errors?.p2?.initials}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="p2.telephonePrivate"
                              label={t('buyersGuide.buyersInfo.phonePrivate')}
                              value={deNormalizePhoneNumber(value.p2?.telephonePrivate)}
                              onChange={handleChange}
                              size="small"
                              autoComplete="off"
                              fullWidth
                              error={!!errors?.p2?.telephonePrivate}
                              helperText={errors?.p2?.telephonePrivate}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="p2.telephoneWork"
                              label={t('buyersGuide.buyersInfo.phoneWork')}
                              value={deNormalizePhoneNumber(value.p2?.telephoneWork)}
                              onChange={handleChange}
                              size="small"
                              autoComplete="off"
                              fullWidth
                              error={!!errors?.p2?.telephoneWork}
                              helperText={errors?.p2?.telephoneWork}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="p2.mobile"
                              label={t('buyersGuide.mobile')}
                              value={value.p2?.mobile}
                              onChange={handlePhoneChange}
                              size="small"
                              autoComplete="off"
                              fullWidth
                              error={!!errors?.p2?.mobile}
                              helperText={errors?.p2?.mobile}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="p2.emailPrivate"
                              label={t('general.emailPrivate')}
                              value={value.p2?.emailPrivate}
                              onChange={handleChange}
                              size="small"
                              autoComplete="off"
                              fullWidth
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="p2.emailWork"
                              label={t('buyersGuide.buyersInfo.emailWork')}
                              value={value.p2?.emailWork}
                              onChange={handleChange}
                              size="small"
                              autoComplete="off"
                              fullWidth
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>)
                  }

                  {!isBuyerAsPerson &&
                    <Grid container spacing={1} sx={{ px: 2 }}>
                      <Grid size={{ xs: 12 }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="relation.firstName"
                              label={t('general.firstName')}
                              value={value.relation?.firstName || ''}
                              onChange={handleChange}
                              size="small"
                              fullWidth
                              error={!!errors?.relation?.firstName}
                              helperText={errors?.relation?.firstName}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="relation.lastName"
                              label={t('general.lastName')}
                              value={value.relation?.lastName || ''}
                              onChange={handleChange}
                              size="small"
                              fullWidth
                              error={!!errors?.relation?.lastName}
                              helperText={errors?.relation?.lastName}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <FormControl size="small" fullWidth>
                               <Autocomplete
                              freeSolo
                              key={new Date().getTime()}
                              options={dropdownValues?.middleNames?.map(({ id, name }) =>  name)}
                              value={value.relation?.middleName}
                              filterOptions={(options, params) => {
                                  const filtered = filter(options, params);
                                  if (params.inputValue.includes(params.inputValue)) {
                                      filtered.push(params.inputValue);
                                  }
                                  return filtered;
                              }}
                              inputValue={value.relation?.middleName}
                              onInputChange={(e, newValue) => {
                              handleChange({
                                target: {
                                  name: "relation.middleName",
                                  value: newValue
                                }
                              });
                             }}
                              renderInput={(params) => (
                                  <TextField
                                      {...params}
                                      name="relation.middleName"
                                      label={t("general.namePrefixes")}
                                      variant="outlined"
                                      size="small"
                                      margin="dense"
                                      slotProps={{
                                          htmlInput: {
                                              ...params.inputProps,
                                              maxLength: 15
                                          },

                                          inputLabel: {
                                              shrink: true,
                                          }
                                      }}
                                      error={!!errors?.relation?.middleName}
                                      helperText={errors?.relation?.middleName}
                                      />
                              )}
                          />
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="relation.initials"
                              label={t('general.initials')}
                              value={value.relation?.initials || ''}
                              onChange={handleChange}
                              size="small"
                              fullWidth
                              error={!!errors?.relation?.initials}
                              helperText={errors?.relation?.initials}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="relation.relationFunctionName"
                              label={t('general.function')}
                              value={value.relation?.relationFunctionName || ''}
                              onChange={handleChange}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="relation.relationDepartment"
                              label={t('general.department')}
                              value={value.relation?.relationDepartment || ''}
                              onChange={handleChange}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="relation.relationEmail"
                              label={t('buyersGuide.businessEmail')}
                              value={value.relation?.relationEmail || ''}
                              onChange={handleChange}
                              size="small"
                              fullWidth
                            />
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="relation.relationMobile"
                              label={t('buyersGuide.mobile')}
                              value={value.relation?.relationMobile || ''}
                              onChange={handlePhoneChange}
                              size="small"
                              fullWidth
                              error={!!errors?.relation?.relationMobile}
                              helperText={errors?.relation?.relationMobile}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              name="relation.relationTelephone"
                              label={t('general.telephone')}
                              value={deNormalizePhoneNumber(value.relation?.relationTelephone || '')}
                              onChange={handleChange}
                              size="small"
                              fullWidth
                              error={!!errors?.relation?.relationTelephone}
                              helperText={errors?.relation?.relationTelephone}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  }

                </Grid>
              </Grid>
            )}
          </Grid>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default EditBuyerRenterInformationDialog;
